// src/pages/inventory/master-data/material-master/MaterialMaster.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Autocomplete,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../../components/css/ded-style.css';
import useAuthCheck from "../../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../../firebase";
import { ref, onValue, set, remove } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatText
} from "../../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function MaterialMaster() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);
    
    // State untuk data referensi
    const [materialCategoryList, setMaterialCategoryList] = useState([]);
    const [uomList, setUomList] = useState([]);
    
    // State untuk data baru
    const [newData, setNewData] = useState({
        index: '',
        kode_material: '',
        id_material: '',
        deskripsi: '',
        nama_produk: '',
        id_satuan: ''
    });

    // Fungsi untuk mendapatkan nama berdasarkan ID
    const getNamaKategoriMaterial = (id) => {
        const materialCategory = materialCategoryList.find(item => item.id_material === id);
        return materialCategory ? materialCategory.nama_kategori_material : '';
    };

    const getNamaSatuan = (id) => {
        const uom = uomList.find(item => item.id_satuan === id);
        return uom ? uom.nama_satuan : '';
    };

    // Fungsi untuk mendapatkan ID berdasarkan nama
    const getIdMaterialByName = (nama) => {
        const materialCategory = materialCategoryList.find(item => 
            item.nama_kategori_material.toLowerCase().trim() === nama.toLowerCase().trim()
        );
        return materialCategory ? materialCategory.id_material : '';
    };

    const getIdSatuanByName = (nama) => {
        const uom = uomList.find(item => 
            item.nama_satuan.toLowerCase().trim() === nama.toLowerCase().trim()
        );
        return uom ? uom.id_satuan : '';
    };

    // Fungsi untuk mengecek apakah kode material sudah ada (case-insensitive)
    const isKodeMaterialExists = (kodeToCheck) => {
        if (!kodeToCheck || !originalDataList) return false;
        
        const kodeLower = kodeToCheck.toLowerCase().trim();
        return originalDataList.some(item => 
            item.kode_material.toLowerCase().trim() === kodeLower
        );
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch data referensi terlebih dahulu
                await fetchReferensiData();
                
                // Fetch main data
                await fetchMainData();
                
            } catch (error) {
                console.error("Error fetching all data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    const fetchReferensiData = async () => {
        try {
            // Fetch material category
            const materialCategoryRef = ref(database, "db_inventory/db_master_data/tb_material_category");
            const materialCategorySnapshot = await onValuePromise(materialCategoryRef);
            const materialCategoryData = materialCategorySnapshot.val();
            const materialCategoryArray = [];
            
            if (materialCategoryData) {
                Object.keys(materialCategoryData).forEach(key => {
                    const record = materialCategoryData[key];
                    materialCategoryArray.push({ 
                        id_material: record.id_material || key,
                        nama_kategori_material: record.nama_kategori_material || ''
                    });
                });
            }
            setMaterialCategoryList(materialCategoryArray);

            // Fetch uom
            const uomRef = ref(database, "db_inventory/db_master_data/tb_uom");
            const uomSnapshot = await onValuePromise(uomRef);
            const uomData = uomSnapshot.val();
            const uomArray = [];
            
            if (uomData) {
                Object.keys(uomData).forEach(key => {
                    const record = uomData[key];
                    uomArray.push({ 
                        id_satuan: record.id_satuan || key,
                        nama_satuan: record.nama_satuan || ''
                    });
                });
            }
            setUomList(uomArray);

        } catch (error) {
            console.error("Error fetching referensi data:", error);
            throw error;
        }
    };

    const fetchMainData = async () => {
        try {
            // Fetch main data from db_inventory/db_master_data/tb_material_master
            const materialMasterRef = ref(database, "db_inventory/db_master_data/tb_material_master");
            const materialMasterSnapshot = await onValuePromise(materialMasterRef);
            const materialMasterData = materialMasterSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (materialMasterData) {
                Object.keys(materialMasterData).forEach(key => {
                    const record = materialMasterData[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        index: record.index || '',
                        kode_material: record.kode_material || key,
                        id_material: record.id_material || '',
                        deskripsi: record.deskripsi || '',
                        nama_produk: record.nama_produk || '',
                        id_satuan: record.id_satuan || '',
                        last_updated: record.last_updated || null
                    });
                    
                    // Cari waktu update terakhir
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                // Set last updated time
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            // Sort by index (ascending)
            loadedData.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);

        } catch (error) {
            console.error("Error fetching main data:", error);
            throw error;
        }
    };

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.index?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.kode_material?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                getNamaKategoriMaterial(item.id_material)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.deskripsi?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.nama_produk?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                getNamaSatuan(item.id_satuan)?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList, materialCategoryList, uomList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Baca data dari Excel
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    console.log("Data Excel:", jsonData);
                    
                    // Cari header
                    const headers = jsonData[0] || [];
                    console.log("Headers:", headers);
                    
                    // Data dimulai dari baris 2 (indeks 2 karena array mulai dari 0)
                    // Format: Kolom 0 = Index, Kolom 1 = Kategori Material, Kolom 2 = Kode Material, 
                    // Kolom 3 = Deskripsi, Kolom 4 = Nama Produk, Kolom 5 = UoM
                    const dataRows = jsonData.slice(2);
                    console.log("Data rows:", dataRows);

                    // Path database
                    const dbPath = "db_inventory/db_master_data/tb_material_master";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    for (const row of dataRows) {
                        try {
                            // Skip baris kosong
                            if (!row || row.length === 0 || (!row[0] && !row[1])) {
                                ignoredCount++;
                                continue;
                            }

                            const index = row[0]?.toString().trim() || '';
                            const kategoriMaterial = row[1]?.toString().trim() || '';
                            const kodeMaterial = row[2]?.toString().trim() || '';
                            const deskripsi = row[3]?.toString().trim() || '';
                            const namaProduk = row[4]?.toString().trim() || '';
                            const uom = row[5]?.toString().trim() || '';

                            console.log("Processing row:", { index, kategoriMaterial, kodeMaterial, deskripsi, namaProduk, uom });

                            // Validasi: Kode Material wajib ada
                            if (!kodeMaterial) {
                                console.log("Kode Material kosong, diabaikan");
                                ignoredCount++;
                                continue;
                            }

                            // Cari ID berdasarkan nama
                            const idMaterial = getIdMaterialByName(kategoriMaterial);
                            const idSatuan = getIdSatuanByName(uom);

                            // Persiapkan data untuk disimpan
                            const dataToSave = {
                                index: index,
                                kode_material: kodeMaterial,
                                id_material: idMaterial,
                                deskripsi: deskripsi,
                                nama_produk: namaProduk,
                                id_satuan: idSatuan,
                                last_updated: new Date().toISOString()
                            };

                            console.log("Data yang akan disimpan:", dataToSave);

                            // Cek apakah kode material sudah ada (case-insensitive)
                            const existingKey = Object.keys(existingData).find(key => 
                                existingData[key].kode_material?.toLowerCase().trim() === kodeMaterial.toLowerCase().trim()
                            );

                            if (existingKey) {
                                // Update data yang sudah ada
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, dataToSave);
                                updatedCount++;
                                console.log(`Data diperbarui: ${kodeMaterial}`);
                            } else {
                                // Simpan sebagai data baru dengan key = kode_material
                                const newRef = ref(database, `${dbPath}/${kodeMaterial}`);
                                await set(newRef, dataToSave);
                                importedCount++;
                                console.log(`Data baru: ${kodeMaterial}`);
                            }

                        } catch (error) {
                            console.error("Error processing row:", error, "Row data:", row);
                            errors.push(`Error pada baris: ${JSON.stringify(row)} - ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await fetchMainData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (data kosong/tidak valid).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                    console.log(`Import summary: ${importedCount} baru, ${updatedCount} diperbarui, ${ignoredCount} diabaikan`);

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    const handleExport = () => {
        try {
            // Siapkan data untuk export
            const exportData = dataList.map(item => ({
                'Index': item.index,
                'Kategori Material': getNamaKategoriMaterial(item.id_material),
                'Kode Material': item.kode_material,
                'Deskripsi': item.deskripsi,
                'Nama Produk': item.nama_produk,
                'UoM': getNamaSatuan(item.id_satuan)
            }));

            // Buat worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            
            // Buat workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Material Master");
            
            // Simpan file
            XLSX.writeFile(wb, `Material_Master_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diexport.",
                severity: "success",
            });
            
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackbar({
                open: true,
                message: "Gagal mengexport data.",
                severity: "error",
            });
        }
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            index: row.index,
            kode_material: row.kode_material,
            id_material: row.id_material,
            deskripsi: row.deskripsi,
            nama_produk: row.nama_produk,
            id_satuan: row.id_satuan
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Update data di Firebase
            const dataRef = ref(database, `db_inventory/db_master_data/tb_material_master/${firebaseKey}`);
            const updatedData = {
                ...editFormData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state dengan membuat array baru
            const updatedDataList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            // Sort by index
            updatedDataList.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });
            
            // Buat array baru untuk originalDataList
            const updatedOriginalList = originalDataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            updatedOriginalList.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });
            
            // Set state dengan array baru
            setDataList([...updatedDataList]);
            setOriginalDataList([...updatedOriginalList]);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, kodeMaterial) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Kode Material: ${kodeMaterial}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_inventory/db_master_data/tb_material_master/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state dengan array baru
                const updatedDataList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                const updatedOriginalList = originalDataList.filter(item => item.firebaseKey !== firebaseKey);
                
                setDataList([...updatedDataList]);
                setOriginalDataList([...updatedOriginalList]);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
        // Reset form ketika membuka form
        if (!showAddForm) {
            setNewData({
                index: '',
                kode_material: '',
                id_material: '',
                deskripsi: '',
                nama_produk: '',
                id_satuan: ''
            });
        }
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        // Validasi: kode material tidak boleh kosong
        if (!newData.kode_material.trim()) {
            setSnackbar({
                open: true,
                message: "Kode Material tidak boleh kosong.",
                severity: "warning",
            });
            return;
        }

        // Cek apakah kode material sudah ada (case-insensitive)
        if (isKodeMaterialExists(newData.kode_material)) {
            setSnackbar({
                open: true,
                message: `Kode Material "${newData.kode_material}" sudah ada dalam database.`,
                severity: "warning",
            });
            return;
        }

        try {
            // Simpan ke Firebase dengan key = kode_material
            const dataRef = ref(database, `db_inventory/db_master_data/tb_material_master/${newData.kode_material}`);
            const dataToSave = {
                ...newData,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, dataToSave);
            
            // Update local state dengan array baru
            const newItem = {
                firebaseKey: newData.kode_material,
                ...dataToSave
            };
            
            const updatedList = [...originalDataList, newItem];
            
            // Sort by index
            updatedList.sort((a, b) => {
                const indexA = a.index ? parseInt(a.index) : 0;
                const indexB = b.index ? parseInt(b.index) : 0;
                return indexA - indexB;
            });
            
            setDataList([...updatedList]);
            setOriginalDataList([...updatedList]);
            
            // Reset form dan state
            setNewData({
                index: '',
                kode_material: '',
                id_material: '',
                deskripsi: '',
                nama_produk: '',
                id_satuan: ''
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            index: '',
            kode_material: '',
            id_material: '',
            deskripsi: '',
            nama_produk: '',
            id_satuan: ''
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Material Master
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            <TextField
                                label="Index"
                                size="small"
                                value={newData.index}
                                onChange={(e) => handleNewDataChange('index', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Kode Material"
                                size="small"
                                value={newData.kode_material}
                                onChange={(e) => handleNewDataChange('kode_material', e.target.value)}
                                required
                                fullWidth
                                error={isKodeMaterialExists(newData.kode_material)}
                                helperText={isKodeMaterialExists(newData.kode_material) ? "Kode Material sudah ada dalam database" : ""}
                            />
                            <Autocomplete
                                options={materialCategoryList}
                                getOptionLabel={(option) => option.nama_kategori_material || ''}
                                value={materialCategoryList.find(item => item.id_material === newData.id_material) || null}
                                onChange={(event, newValue) => {
                                    handleNewDataChange('id_material', newValue ? newValue.id_material : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Kategori Material"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                            <Autocomplete
                                options={uomList}
                                getOptionLabel={(option) => option.nama_satuan || ''}
                                value={uomList.find(item => item.id_satuan === newData.id_satuan) || null}
                                onChange={(event, newValue) => {
                                    handleNewDataChange('id_satuan', newValue ? newValue.id_satuan : '');
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="UoM"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                            <TextField
                                label="Deskripsi"
                                size="small"
                                value={newData.deskripsi}
                                onChange={(e) => handleNewDataChange('deskripsi', e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Nama Produk"
                                size="small"
                                value={newData.nama_produk}
                                onChange={(e) => handleNewDataChange('nama_produk', e.target.value)}
                                fullWidth
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button variant="contained" onClick={handleSaveNew} disabled={isKodeMaterialExists(newData.kode_material)}>
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="data material master table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">Index</TableCell>
                                        <TableCell className="ded-tch">Kategori Material</TableCell>
                                        <TableCell className="ded-tch">Kode Material</TableCell>
                                        <TableCell className="ded-tch">Deskripsi</TableCell>
                                        <TableCell className="ded-tch">Nama Produk</TableCell>
                                        <TableCell className="ded-tch">UoM</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.index}
                                                                onChange={(e) => handleEditChange('index', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                options={materialCategoryList}
                                                                getOptionLabel={(option) => option.nama_kategori_material || ''}
                                                                value={materialCategoryList.find(item => item.id_material === editFormData.id_material) || null}
                                                                onChange={(event, newValue) => {
                                                                    handleEditChange('id_material', newValue ? newValue.id_material : '');
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.kode_material}
                                                                disabled // Kunci input Kode Material saat edit
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiInputBase-input.Mui-disabled': {
                                                                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                                                        backgroundColor: 'action.disabledBackground'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.deskripsi}
                                                                onChange={(e) => handleEditChange('deskripsi', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.nama_produk}
                                                                onChange={(e) => handleEditChange('nama_produk', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                options={uomList}
                                                                getOptionLabel={(option) => option.nama_satuan || ''}
                                                                value={uomList.find(item => item.id_satuan === editFormData.id_satuan) || null}
                                                                onChange={(event, newValue) => {
                                                                    handleEditChange('id_satuan', newValue ? newValue.id_satuan : '');
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.index)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(getNamaKategoriMaterial(row.id_material))}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.kode_material)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(row.deskripsi)}</TableCell>
                                                        <TableCell>{formatText(row.nama_produk)}</TableCell>
                                                        <TableCell>{formatText(getNamaSatuan(row.id_satuan))}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.kode_material)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MaterialMaster;